import { useQueries } from '@tanstack/react-query';
import {
  getVaccinations,
  getMedicalUpdates,
  getGroomingRecords,
} from '@/api/pets';
export interface PetForCalendar {
  id: string;
  name: string;
  species: string;
}

const INSURANCE_STORAGE_KEY = (petId: string) => `petso_insurance_${petId}`;

export type RecordEventType =
  | 'vaccination-administered'
  | 'vaccination-expiry'
  | 'vaccination-booster'
  | 'medical-update'
  | 'medical-followup'
  | 'grooming'
  | 'insurance-travel-expiry'
  | 'insurance-health-expiry';

export interface RecordCalendarEvent {
  id: string;
  title: string;
  date: string;
  time: string;
  type: RecordEventType;
  petId: string;
  petName: string;
  petSpecies?: string;
  recordId: string;
  notes?: string;
  isDue?: boolean;
  isOverdue?: boolean;
}

function loadInsuranceForPet(petId: string): {
  travel?: { coverageEnd?: string };
  health?: { coverageEnd?: string };
} {
  try {
    const stored = localStorage.getItem(INSURANCE_STORAGE_KEY(petId));
    if (stored) {
      const parsed = JSON.parse(stored);
      return typeof parsed === 'object' && parsed !== null ? parsed : {};
    }
  } catch {
    // ignore
  }
  return {};
}

export function useCalendarRecordEvents(pets: PetForCalendar[]) {
  const safePets = Array.isArray(pets) ? pets : [];
  const petIds = safePets.map((p) => p.id);

  const vaccinationQueries = useQueries({
    queries: petIds.map((petId) => ({
      queryKey: ['petRecords', 'vaccinations', petId],
      queryFn: async () => {
        const res = await getVaccinations(petId);
        return { petId, vaccinations: res.vaccinations };
      },
      staleTime: 1000 * 60 * 5,
    })),
  });

  const medicalQueries = useQueries({
    queries: petIds.map((petId) => ({
      queryKey: ['petRecords', 'medicalUpdates', petId],
      queryFn: async () => {
        const res = await getMedicalUpdates(petId);
        return { petId, medicalUpdates: res.medicalUpdates };
      },
      staleTime: 1000 * 60 * 5,
    })),
  });

  const groomingQueries = useQueries({
    queries: petIds.map((petId) => ({
      queryKey: ['petRecords', 'grooming', petId],
      queryFn: async () => {
        const res = await getGroomingRecords(petId);
        return { petId, groomingRecords: res.groomingRecords };
      },
      staleTime: 1000 * 60 * 5,
    })),
  });

  const events: RecordCalendarEvent[] = [];

  const getPet = (id: string) => safePets.find((p) => p.id === id);

  vaccinationQueries.forEach((q) => {
    const data = q.data;
    if (!data) return;
    const pet = getPet(data.petId);
    if (!pet) return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    (data.vaccinations ?? []).forEach(
      (v: {
        id: string;
        vaccineName: string;
        dateAdministered: string;
        expiryDate: string;
        boosterDate?: string;
      }) => {
        if (v.dateAdministered) {
          events.push({
            id: `vax-admin-${v.id}`,
            title: `${v.vaccineName} administered`,
            date: v.dateAdministered.split('T')[0],
            time: '09:00',
            type: 'vaccination-administered',
            petId: pet.id,
            petName: pet.name,
            petSpecies: pet.species,
            recordId: v.id,
          });
        }
        if (v.expiryDate) {
          const expiry = new Date(v.expiryDate);
          expiry.setHours(0, 0, 0, 0);
          events.push({
            id: `vax-expiry-${v.id}`,
            title: `${v.vaccineName} expires`,
            date: v.expiryDate.split('T')[0],
            time: '09:00',
            type: 'vaccination-expiry',
            petId: pet.id,
            petName: pet.name,
            petSpecies: pet.species,
            recordId: v.id,
            isDue:
              expiry.getTime() <= today.getTime() + 30 * 24 * 60 * 60 * 1000,
            isOverdue: expiry < today,
          });
        }
        if (v.boosterDate) {
          events.push({
            id: `vax-booster-${v.id}`,
            title: `${v.vaccineName} booster due`,
            date: v.boosterDate.split('T')[0],
            time: '09:00',
            type: 'vaccination-booster',
            petId: pet.id,
            petName: pet.name,
            petSpecies: pet.species,
            recordId: v.id,
          });
        }
      }
    );
  });

  medicalQueries.forEach((q) => {
    const data = q.data;
    if (!data) return;
    const pet = getPet(data.petId);
    if (!pet) return;

    (data.medicalUpdates ?? []).forEach(
      (m: {
        id: string;
        title: string;
        date: string;
        followUpDate?: string;
        updateType: string;
      }) => {
        if (m.date) {
          events.push({
            id: `med-${m.id}`,
            title: `${m.title} (${m.updateType})`,
            date: m.date.split('T')[0],
            time: '09:00',
            type: 'medical-update',
            petId: pet.id,
            petName: pet.name,
            petSpecies: pet.species,
            recordId: m.id,
          });
        }
        if (m.followUpDate) {
          events.push({
            id: `med-followup-${m.id}`,
            title: `Follow-up: ${m.title}`,
            date: m.followUpDate.split('T')[0],
            time: '09:00',
            type: 'medical-followup',
            petId: pet.id,
            petName: pet.name,
            petSpecies: pet.species,
            recordId: m.id,
          });
        }
      }
    );
  });

  groomingQueries.forEach((q) => {
    const data = q.data;
    if (!data) return;
    const pet = getPet(data.petId);
    if (!pet) return;

    (data.groomingRecords ?? []).forEach(
      (g: { id: string; serviceType: string; serviceDate: string }) => {
        if (g.serviceDate) {
          events.push({
            id: `groom-${g.id}`,
            title: `Grooming: ${g.serviceType}`,
            date: g.serviceDate.split('T')[0],
            time: '09:00',
            type: 'grooming',
            petId: pet.id,
            petName: pet.name,
            petSpecies: pet.species,
            recordId: g.id,
          });
        }
      }
    );
  });

  safePets.forEach((pet) => {
    const insurance = loadInsuranceForPet(pet.id);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (insurance.travel?.coverageEnd) {
      const d = new Date(insurance.travel.coverageEnd.split('T')[0]);
      d.setHours(0, 0, 0, 0);
      events.push({
        id: `ins-travel-exp-${pet.id}`,
        title: 'Travel insurance expires',
        date: insurance.travel.coverageEnd.split('T')[0],
        time: '09:00',
        type: 'insurance-travel-expiry',
        petId: pet.id,
        petName: pet.name,
        petSpecies: pet.species,
        recordId: `insurance-travel-${pet.id}`,
        isDue: d.getTime() <= today.getTime() + 30 * 24 * 60 * 60 * 1000,
        isOverdue: d < today,
      });
    }
    if (insurance.health?.coverageEnd) {
      const d = new Date(insurance.health.coverageEnd.split('T')[0]);
      d.setHours(0, 0, 0, 0);
      events.push({
        id: `ins-health-exp-${pet.id}`,
        title: 'Health insurance expires',
        date: insurance.health.coverageEnd.split('T')[0],
        time: '09:00',
        type: 'insurance-health-expiry',
        petId: pet.id,
        petName: pet.name,
        petSpecies: pet.species,
        recordId: `insurance-health-${pet.id}`,
        isDue: d.getTime() <= today.getTime() + 30 * 24 * 60 * 60 * 1000,
        isOverdue: d < today,
      });
    }
  });

  const getRecordEventsForDay = (date: Date) => {
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return events
      .filter((e) => {
        const eventDate = new Date(e.date + 'T' + (e.time || '09:00'));
        eventDate.setHours(0, 0, 0, 0);
        return checkDate.toDateString() === eventDate.toDateString();
      })
      .sort((a, b) => {
        const timeA = (a.time || '09:00').replace(':', '');
        const timeB = (b.time || '09:00').replace(':', '');
        return parseInt(timeA, 10) - parseInt(timeB, 10);
      });
  };

  return {
    recordEvents: events,
    getRecordEventsForDay,
    isLoading:
      vaccinationQueries.some((q) => q.isLoading) ||
      medicalQueries.some((q) => q.isLoading) ||
      groomingQueries.some((q) => q.isLoading),
  };
}
